import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

const AppSettings = ({ user }) => {
  const [settings, setSettings] = useState({
    signup_notification_email: '',
    signup_bcc_email: '',
    ai_suggestion_notify: '1'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/app/api/admin/settings.php', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch('/app/api/admin/settings.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully.' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save settings.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Settings</h1>
          <p className="text-gray-600">Configure system-wide settings for MageGuard.</p>
        </div>

        {message && (
          <div className={`mb-6 rounded-lg p-4 flex items-start gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {message.type === 'success'
              ? <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
            }
            <p className={`text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 space-y-6">

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Signup Notifications
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Notification Email
                </label>
                <input
                  type="email"
                  value={settings.signup_notification_email}
                  onChange={(e) => setSettings({ ...settings, signup_notification_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="xyz@gmail.com"
                />
                <p className="mt-1 text-xs text-gray-500">All new signup submissions will be sent to this address.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BCC Email
                </label>
                <input
                  type="email"
                  value={settings.signup_bcc_email}
                  onChange={(e) => setSettings({ ...settings, signup_bcc_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="abc@gmail.com"
                />
                <p className="mt-1 text-xs text-gray-500">This address will be BCC'd on all signup notification emails.</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              AI Suggestions
            </h2>
            <div className="flex items-center gap-3">
              <input
                id="ai_notify"
                type="checkbox"
                checked={settings.ai_suggestion_notify === '1'}
                onChange={(e) => setSettings({ ...settings, ai_suggestion_notify: e.target.checked ? '1' : '0' })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="ai_notify" className="text-sm font-medium text-gray-700">
                Email site managers when a new AI suggestion is added for their site
              </label>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppSettings;
